import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SessionProgressIndicator from '../../components/ui/SessionProgressIndicator';
import CollaborativeStatusBar from '../../components/ui/CollaborativeStatusBar';
import ShareSessionControl from '../../components/ui/ShareSessionControl';
import ParticipantSummaryCard from './components/ParticipantSummaryCard';
import TipCalculator from './components/TipCalculator';
import PaymentMethodModal from './components/PaymentMethodModal';
import PaymentStatusOverview from './components/PaymentStatusOverview';
import DisputeResolutionPanel from './components/DisputeResolutionPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { subscribeToSession, saveSessionToCloud, getSessionFromCloud, getAllSessionClaims, subscribeToSessionClaims } from '../../utils/supabaseService';
import { getCurrentUser, getActiveSessionId } from '../../utils/sessionStorage';


const PaymentSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showDisputePanel, setShowDisputePanel] = useState(false);
  const [realtimeBillData, setRealtimeBillData] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Get data from previous page (item-selection)
  const locationState = location.state || {};

  // 🔄 Recuperar usuario de localStorage si no viene en el state
  const storedUser = getCurrentUser();
  const storedSessionId = getActiveSessionId();

  // Usar datos del state, o recuperar de localStorage como fallback
  const sessionId = locationState.sessionId || storedSessionId;
  const initialBillData = locationState.billData;
  const initialBillItems = locationState.billItems;
  const userName = locationState.userName || storedUser?.userName;
  const isHost = locationState.isHost ?? storedUser?.isHost ?? false;
  const isGuest = locationState.isGuest || !isHost;

  console.log('📦 payment-summary: StoredUser from localStorage:', storedUser);

  // Check if there's an active session
  useEffect(() => {
    if (!sessionId && !initialBillData) {
      setHasActiveSession(false);
      setIsLoading(false);
    }
  }, [sessionId, initialBillData]);

  // Use realtime data if available
  const billData = realtimeBillData || initialBillData;
  const [billItems, setBillItems] = useState(initialBillItems || []);

  console.log('💰 Payment Summary loaded with:', { sessionId, billData, billItems, userName });

  // Load fresh data from Supabase on mount
  useEffect(() => {
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    console.log('🔄 Loading fresh data from Supabase on mount...');

    // Cargar session data Y claims desde user_claims
    Promise.all([
      getSessionFromCloud(sessionId),
      getAllSessionClaims(sessionId)
    ]).then(([sessionResult, claimsResult]) => {
      if (sessionResult.success && sessionResult.data) {
        console.log('✅ Loaded fresh data from Supabase:', sessionResult.data);
        setRealtimeBillData(sessionResult.data);
        setHasActiveSession(true);

        // Transform items with proper price calculation
        const freshItems = sessionResult.data.items?.map(item => {
          const quantity = item.quantity || 1;
          const unitPrice = item.unitPrice || (item.totalPrice / quantity) || item.price || 0;

          // Agregar claims desde user_claims
          let itemClaims = [];
          if (claimsResult.success && claimsResult.data) {
            itemClaims = claimsResult.data
              .filter(claim => claim.item_id === item.id)
              .map(claim => ({
                id: claim.id,
                userId: claim.user_id,
                userName: claim.user_name,
                quantity: claim.quantity,
                unitPrice: unitPrice
              }));
          }

          return {
            id: item.id,
            name: item.name,
            description: item.description || '',
            price: unitPrice,
            unitPrice: unitPrice,
            totalPrice: item.totalPrice || (unitPrice * quantity),
            quantity: quantity,
            category: item.category || 'otros',
            allergens: item.allergens || [],
            validated: item.validated || false,
            claims: itemClaims
          };
        }) || [];

        setBillItems(freshItems);
        console.log('✅ Set fresh billItems with claims from user_claims:', freshItems);
      } else {
        console.error('❌ Failed to load fresh data:', sessionResult.error);
        setHasActiveSession(false);
      }
      setIsLoading(false);
    }).catch(err => {
      console.error('❌ Error loading fresh data:', err);
      setIsLoading(false);
    });
  }, [sessionId]); // Only run once on mount

  // Redirect if no data after trying to load
  useEffect(() => {
    if (!billData || !sessionId) {
      console.error('❌ No bill data or session in payment-summary');
    }
  }, [billData, sessionId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!sessionId) {
      console.log('❌ No sessionId in payment-summary, cannot subscribe');
      return;
    }

    console.log('🔄 Setting up real-time subscription in payment-summary');

    const unsubscribe = subscribeToSession(sessionId, (updatedBillData) => {
      console.log('📡 Real-time update received in payment-summary:', updatedBillData);
      console.log('👥 Updated participants:', updatedBillData?.participants);
      if (updatedBillData) {
        setRealtimeBillData(updatedBillData);

        // Update billItems with claims
        const updatedItems = updatedBillData.items?.map(item => {
          // Calculate unit price correctly
          const quantity = item.quantity || 1;
          const unitPrice = item.unitPrice || (item.totalPrice / quantity) || item.price || 0;

          return {
            id: item.id,
            name: item.name,
            description: item.description || '',
            price: unitPrice,
            unitPrice: unitPrice,
            totalPrice: item.totalPrice || (unitPrice * quantity),
            quantity: quantity,
            category: item.category || 'otros',
            allergens: item.allergens || [],
            validated: item.validated || false,
            claims: item.claims || []
          };
        }) || [];

        // Only update if we have claims data or if this is the first update
        const hasClaims = updatedItems.some(item => item.claims && item.claims.length > 0);
        setBillItems(prevItems => {
          // If we already have items with claims locally, and incoming has no claims, keep local
          const localHasClaims = prevItems.some(item => item.claims && item.claims.length > 0);
          if (localHasClaims && !hasClaims) {
            console.log('⚠️ Keeping local claims, incoming has no claims');
            return prevItems;
          }
          console.log('✅ Updated billItems in payment-summary from realtime');
          return updatedItems;
        });
      }
    });

    return () => {
      console.log('🧹 Cleaning up payment-summary subscription');
      unsubscribe();
    };
  }, [sessionId]);

  // Suscribirse a cambios en user_claims
  useEffect(() => {
    if (!sessionId) return;

    console.log('📡 Subscribing to user_claims in payment-summary...');

    const unsubscribe = subscribeToSessionClaims(sessionId, (payload) => {
      console.log('📡 Claims change in payment-summary:', payload.eventType);

      // Recargar claims
      getAllSessionClaims(sessionId).then(result => {
        if (result.success && result.data) {
          setBillItems(prevItems => {
            return prevItems.map(item => {
              const itemClaims = result.data
                .filter(claim => claim.item_id === item.id)
                .map(claim => ({
                  id: claim.id,
                  userId: claim.user_id,
                  userName: claim.user_name,
                  quantity: claim.quantity,
                  unitPrice: item.unitPrice
                }));

              return {
                ...item,
                claims: itemClaims
              };
            });
          });
        }
      });
    });

    return () => {
      console.log('🧹 Cleaning up claims subscription in payment-summary');
      unsubscribe();
    };
  }, [sessionId]);

  // Tip calculation state
  const [tipCalculation, setTipCalculation] = useState({
    percentage: 0,
    amount: 0,
    perPerson: 0
  });

  // Calculate participants data from billItems
  const calculateParticipants = () => {
    if (!billData?.participants || !billItems) {
      console.log('⚠️ No participants or billItems data', {
        hasBillData: !!billData,
        hasParticipants: !!billData?.participants,
        participantsLength: billData?.participants?.length,
        participants: billData?.participants,
        hasBillItems: !!billItems,
        billItemsLength: billItems?.length
      });
      return [];
    }

    console.log('📊 Calculating participants from:', {
      allParticipants: billData.participants,
      billItems,
      billItemsWithClaims: billItems.filter(item => item.claims && item.claims.length > 0)
    });

    // Group claims by userId
    const participantMap = new Map();

    // Initialize ALL participants (they should all be visible)
    billData.participants.forEach(participant => {
      console.log(`👤 Initializing participant: ${participant.name} (ID: ${participant.id}, Type: ${typeof participant.id})`);
      participantMap.set(participant.id, {
        id: participant.id,
        name: participant.name,
        paymentStatus: participant.paymentStatus || 'unpaid',
        selectedItems: [],
        subtotal: 0,
        taxRate: billData.taxRate || 21,
        taxAmount: 0,
        tipAmount: 0,
        totalAmount: 0
      });
    });

    // Add claimed items to each participant
    billItems.forEach(item => {
      if (item.claims && item.claims.length > 0) {
        item.claims.forEach(claim => {
          console.log(`🔍 Processing claim:`, {
            itemName: item.name,
            claimUserId: claim.userId,
            claimUserName: claim.userName,
            claimQuantity: claim.quantity,
            participantIds: Array.from(participantMap.keys())
          });

          // Usar claim.userId para buscar el participante
          const claimUserIdStr = String(claim.userId);
          let participant = null;

          // Buscar el participant comparando como strings
          for (const [key, value] of participantMap.entries()) {
            if (String(key) === claimUserIdStr) {
              participant = value;
              break;
            }
          }

          if (participant) {
            const itemTotal = item.price * claim.quantity;
            participant.selectedItems.push({
              id: item.id,
              name: item.name,
              quantity: claim.quantity,
              unitPrice: item.price,
              totalPrice: itemTotal
            });
            participant.subtotal += itemTotal;
            console.log(`💰 Added item to ${participant.name}:`, item.name, 'x', claim.quantity, '=', itemTotal);
          } else {
            console.warn('⚠️ Claim for unknown user:', claim.userId, 'Type:', typeof claim.userId, 'Available participants:', Array.from(participantMap.keys()));
          }
        });
      }
    });

    // Calculate taxes and tips
    participantMap.forEach(participant => {
      // IVA ya incluido en los precios del ticket, no calcular de nuevo
      // participant.taxAmount = participant.subtotal * (participant.taxRate / 100);
      participant.taxAmount = 0;
      participant.tipAmount = participant.subtotal * (tipCalculation.percentage / 100);
      participant.totalAmount = participant.subtotal + participant.taxAmount + participant.tipAmount;
    });

    const result = Array.from(participantMap.values());
    console.log('✅ Calculated participants with details:', result);
    return result;
  };

  const [participants, setParticipants] = useState(() => {
    const initial = calculateParticipants();
    console.log('🎬 Initial participants state:', initial);
    return initial;
  });

  // Recalculate participants when billItems or billData changes
  useEffect(() => {
    console.log('🔄 Recalculating participants due to change in:', { billItems, billData, realtimeBillData });
    const newParticipants = calculateParticipants();
    setParticipants(newParticipants);
    console.log('💰 Recalculated participants:', newParticipants);
  }, [billItems, billData, realtimeBillData, tipCalculation.percentage]);

  // Get current user ID (find participant that matches userName)
  const currentUser = participants.find(p => p.name === userName)?.id || null;
  console.log('👤 Current user:', userName, 'ID:', currentUser);

  // Calculate total tip when participants change
  useEffect(() => {
    const totalSubtotal = participants.reduce((sum, p) => sum + p.subtotal, 0);
    const tipAmount = totalSubtotal * (tipCalculation.percentage / 100);
    const perPerson = participants.length > 0 ? tipAmount / participants.length : 0;

    console.log('💵 Tip calculation:', { totalSubtotal, tipAmount, perPerson, percentage: tipCalculation.percentage });

    setTipCalculation(prev => ({
      ...prev,
      amount: tipAmount,
      perPerson: perPerson
    }));
  }, [participants, tipCalculation.percentage]);

  // Mock disputes data (TODO: implement real disputes)
  const [disputes, setDisputes] = useState([]);

  const totalBill = participants?.reduce((sum, p) => sum + p?.totalAmount, 0);
  const subtotalBill = participants?.reduce((sum, p) => sum + p?.subtotal, 0);

  console.log('💰 SUBTOTAL CALCULATION:', {
    participants,
    subtotalBill,
    participantSubtotals: participants?.map(p => ({ name: p.name, subtotal: p.subtotal, items: p.selectedItems }))
  });

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const handlePaymentMethodSelect = (participantId) => {
    const participant = participants?.find(p => p?.id === participantId);
    setSelectedParticipant(participant);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = (participantId, paymentMethod) => {
    setParticipants(prev => prev?.map(p =>
      p?.id === participantId
        ? { ...p, paymentStatus: 'paid', paymentMethod }
        : p
    ));
  };

  const handleMarkAsPaid = (participantId) => {
    setParticipants(prev => prev?.map(p =>
      p?.id === participantId
        ? { ...p, paymentStatus: 'paid', paymentMethod: 'cash' }
        : p
    ));
  };

  const handleExportReceipt = (participantId) => {
    const participant = participants?.find(p => p?.id === participantId);
    console.log(`Exporting receipt for ${participant?.name}`);
    // Simulate receipt export
    alert(`Recibo exportado para ${participant?.name}`);
  };

  const handleTipChange = (tipData) => {
    setTipCalculation(tipData);

    // Update participant tip amounts
    setParticipants(prev => prev?.map(p => ({
      ...p,
      tipAmount: tipData?.perPerson,
      totalAmount: p?.subtotal + p?.taxAmount + tipData?.perPerson
    })));
  };

  const handleSendReminder = (participantId) => {
    const participant = participants?.find(p => p?.id === participantId);
    console.log(`Sending reminder to ${participant?.name}`);
    alert(`Recordatorio enviado a ${participant?.name}`);
  };

  const handleCompleteSession = () => {
    console.log('Session completed');
    alert('¡Sesión completada! Todos los pagos han sido procesados.');
    navigate('/session-management');
  };

  const handleExportAllReceipts = () => {
    console.log('Exporting all receipts');
    alert('Todos los recibos han sido exportados');
  };

  const handleCreateDispute = (disputeData) => {
    setDisputes(prev => [...prev, disputeData]);
  };

  const handleResolveDispute = (disputeId, resolution) => {
    setDisputes(prev => prev?.map(d =>
      d?.id === disputeId
        ? { ...d, status: resolution, resolvedAt: new Date()?.toISOString() }
        : d
    ));
  };

  const handleInviteParticipant = () => {
    console.log('Inviting new participant');
    // Hacer scroll al ShareSessionControl
    const shareControl = document.querySelector('[class*="ShareSessionControl"]') ||
      document.querySelector('img[alt*="QR Code"]')?.closest('.bg-surface');
    if (shareControl) {
      shareControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight temporalmente
      shareControl.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
      setTimeout(() => {
        shareControl.style.boxShadow = '';
      }, 2000);
    }
  };

  const handleViewParticipants = () => {
    console.log('Viewing all participants');
  };

  const handleGenerateQR = () => {
    console.log('Generating new QR code');
  };

  const handleCopyLink = (url) => {
    console.log('Link copied:', url);
  };

  const handleShareWhatsApp = (url) => {
    console.log('Sharing via WhatsApp:', url);
  };

  const handleShareEmail = (url) => {
    console.log('Sharing via email:', url);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SessionProgressIndicator currentStep={3} />

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Icon name="Loader" size={48} className="text-primary animate-spin mb-4" />
          <p className="text-text-secondary">Cargando datos de pago...</p>
        </div>
      )}

      {/* No active session */}
      {!isLoading && !hasActiveSession && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-text-secondary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              No existen datos
            </h2>
            <p className="text-text-secondary mb-6">
              No hay una sesión activa. Por favor, escanea un ticket primero para crear una nueva sesión.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/receipt-scanner')}
              iconName="Camera"
              iconPosition="left"
            >
              Escanear Ticket
            </Button>
          </div>
        </div>
      )}

      {/* Active session content */}
      {!isLoading && hasActiveSession && (
        <>
          <CollaborativeStatusBar
            participants={billData?.participants || []}
            sessionStatus="active"
            sessionId={sessionId}
            currentUserId={currentUser}
            onInviteParticipant={handleInviteParticipant}
            onViewParticipants={handleViewParticipants}
          />
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Resumen de Pagos
                </h1>
                <p className="text-text-secondary">
                  Revisa y completa los pagos individuales de cada participante
                </p>
              </div>

              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <Button
                  variant="outline"
                  onClick={() => setShowDisputePanel(!showDisputePanel)}
                  iconName="AlertCircle"
                  iconPosition="left"
                  iconSize={16}
                >
                  {disputes?.filter(d => d?.status === 'pending')?.length > 0 && (
                    <span className="mr-2 bg-error text-error-foreground text-xs px-1.5 py-0.5 rounded-full">
                      {disputes?.filter(d => d?.status === 'pending')?.length}
                    </span>
                  )}
                  Disputas
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigate('/item-selection', {
                    state: {
                      sessionId,
                      billData,
                      userName,
                      isHost,
                      isGuest
                    }
                  })}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  iconSize={16}
                >
                  Volver a Selección
                </Button>
              </div>
            </div>

            {/* Bill Summary */}
            <div className="mb-6 p-4 bg-card border border-border rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-foreground font-mono">
                    {formatAmount(subtotalBill)}
                  </div>
                  <div className="text-sm text-text-secondary">Subtotal</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground font-mono">
                    {formatAmount(tipCalculation?.amount)}
                  </div>
                  <div className="text-sm text-text-secondary">Propina Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground font-mono">
                    {participants?.length}
                  </div>
                  <div className="text-sm text-text-secondary">Participantes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary font-mono">
                    {formatAmount(totalBill)}
                  </div>
                  <div className="text-sm text-text-secondary">Total Final</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Left Column - Participant Summaries */}
              <div className="xl:col-span-2 space-y-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  Resúmenes Individuales
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {participants?.map((participant) => (
                    <ParticipantSummaryCard
                      key={participant?.id}
                      participant={participant}
                      isCurrentUser={participant?.id === currentUser}
                      onPaymentMethodSelect={handlePaymentMethodSelect}
                      onExportReceipt={handleExportReceipt}
                      onMarkAsPaid={handleMarkAsPaid}
                    />
                  ))}
                </div>

                {/* Dispute Resolution Panel */}
                {showDisputePanel && (
                  <div className="mt-6">
                    <DisputeResolutionPanel
                      disputes={disputes}
                      participants={participants}
                      onCreateDispute={handleCreateDispute}
                      onResolveDispute={handleResolveDispute}
                    />
                  </div>
                )}
              </div>

              {/* Right Column - Controls */}
              <div className="space-y-6">
                {/* Tip Calculator */}
                <TipCalculator
                  subtotal={subtotalBill}
                  participants={participants}
                  currentUserId={currentUser}
                  currentTip={tipCalculation}
                  onTipChange={handleTipChange}
                />

                {/* Payment Status Overview */}
                <PaymentStatusOverview
                  participants={participants}
                  totalBill={totalBill}
                  currentUserId={currentUser}
                  onSendReminder={handleSendReminder}
                  onCompleteSession={handleCompleteSession}
                  onExportAllReceipts={handleExportAllReceipts}
                />

                {/* Share Session Control */}
                <ShareSessionControl
                  sessionId={sessionId}
                  position="inline"
                  onGenerateQR={handleGenerateQR}
                  onCopyLink={handleCopyLink}
                  onShareWhatsApp={handleShareWhatsApp}
                  onShareEmail={handleShareEmail}
                />
              </div>
            </div>
          </div>
        </>
      )}
      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        participant={selectedParticipant}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedParticipant(null);
        }}
        onPaymentComplete={handlePaymentComplete}
      />
    </div>
  );
};

export default PaymentSummary;